import type { BomItem, Supplier } from '../../types/supplyChain';
import { buildRfqId } from './rfqTemplate';
import { SCM_DATA_DISCLAIMER_SHORT } from '../../lib/scmDisclaimer';

export interface SupplierOutreachContext {
  siteId: string;
  siteName: string;
  siteAddress: string;
  cod?: string | null;
  technologyLabel?: string | null;
  issuingCompany?: string;
  contactName?: string;
  contactEmail?: string;
}

export interface BuiltSupplierOutreachEmail {
  toEmail: string | undefined;
  subject: string;
  body: string;
  mailtoUrl: string;
}

function fmtDate(iso?: string | null): string {
  if (!iso) return 'TBD';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}

export function buildSupplierOutreachEmail(args: {
  supplier: Supplier;
  bomItem: BomItem;
  project: SupplierOutreachContext;
  responseDeadline?: string;
  rfqSeq?: number;
}): BuiltSupplierOutreachEmail {
  const { supplier, bomItem, project } = args;
  const deadline = args.responseDeadline ?? new Date(Date.now() + 30 * 86400000).toISOString();
  const rfqId = buildRfqId(project.siteId, bomItem.id, args.rfqSeq ?? 1);
  const compliance = (bomItem.complianceRequirements ?? []).join(', ') || 'None listed';
  const toEmail = supplier.contacts?.find(c => c.email)?.email;

  const subject = `RFQ ${rfqId} — ${bomItem.name} — ${project.siteName}`;

  const body = [
    `Dear ${supplier.name} team,`,
    '',
    `Datacrate is requesting availability, pricing, and lead-time for the equipment line below.`,
    '',
    `Project: ${project.siteName}`,
    `Site: ${project.siteAddress}`,
    `Target commercial operations date: ${fmtDate(project.cod)}`,
    project.technologyLabel ? `Technology: ${project.technologyLabel}` : '',
    '',
    `RFQ reference: ${rfqId}`,
    `Response requested by: ${fmtDate(deadline)}`,
    '',
    '--- Equipment specification ---',
    `Item: ${bomItem.name}`,
    bomItem.spec ? `Spec: ${bomItem.spec}` : '',
    `Quantity: ${bomItem.quantity} ${bomItem.unit}`,
    `Category: ${bomItem.category.replace(/-/g, ' ')}`,
    `Compliance requirements: ${compliance}`,
    bomItem.requiredOnSiteDate ? `Required on site: ${fmtDate(bomItem.requiredOnSiteDate)}` : '',
    '',
    'Please provide:',
    '- Unit price (USD) and total extended price',
    '- Lead time (months ARO) and expected delivery window',
    '- Quote validity period',
    '- Payment and freight/incoterms terms',
    '- Certification compliance confirmation',
    '- Current production slot / capacity availability',
    '',
    project.contactName || project.contactEmail
      ? `Reply to: ${[project.contactName, project.contactEmail].filter(Boolean).join(' · ')}`
      : '',
    '',
    `Note: ${SCM_DATA_DISCLAIMER_SHORT}`,
    '',
    'Thank you,',
    project.issuingCompany ?? 'Datacrate',
  ].filter(Boolean).join('\n');

  const mailtoBase = toEmail ? `mailto:${encodeURIComponent(toEmail)}` : 'mailto:';
  const mailtoUrl = `${mailtoBase}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return { toEmail, subject, body, mailtoUrl };
}
